import { type Expense, SplitTypeEnum } from "@finway-group/shared/lib/models"
import { type LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { Button, Col, Dropdown, Menu } from "antd"
import type { MenuInfo } from "rc-menu/lib/interface"
import React from "react"
import { MoreHorizontal as DotsIcon, Link as LinkIcon, Plus as PlusIcon, Trash as TrashIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { getPopupAnchor } from "~Shared/utils/popup.utils"

export interface FormAddExpenseItemRowInterface {
    elem: {
        isHospitality: boolean
        expense: Expense
        onAddItem: (value: boolean) => void
        isLinked: boolean
        onSelect: (info: MenuInfo) => void
    }
    span?: number
    md?: number | object
    sm?: number | object
}

const HospitalityExpenseItemRow = ({ elem }: FormAddExpenseItemRowInterface) => {
    const { expense, onAddItem, isHospitality } = elem

    const handleClickAddItemHospitality = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        onAddItem(isHospitality)
    }

    if (expense.splits.length >= 2) {
        return null
    }

    return (
        <Button onClick={handleClickAddItemHospitality}>
            <PlusIcon />
        </Button>
    )
}

function FormAddExpenseItemRowBase({ elem, ...columnProps }: FormAddExpenseItemRowInterface) {
    const { t } = useTranslation()
    const { expense, isLinked, onSelect, isHospitality } = elem

    const checkIfDeleteAllItemsIsDisabled = () => {
        if (expense.splitType !== SplitTypeEnum.LINE_ITEM) {
            return false
        }

        const splits = expense.splits as Array<LineItem>
        return splits.every((split) => split.purchaseOrderLink)
    }

    const renderDropDownMenu = () => (
        <Menu onClick={onSelect} className="min-w-190">
            <>
                {expense.splitType === SplitTypeEnum.SPLIT || expense.splits.length === 0 ? (
                    <Menu.Item key="add_item" className="py-8" icon={<PlusIcon size={12} />}>
                        <span>{t("action:request.item.add")}</span>
                    </Menu.Item>
                ) : (
                    <Menu.Item
                        key="delete_all_items"
                        className={`py-8 ${checkIfDeleteAllItemsIsDisabled() ? "opacity-50" : ""}`}
                        icon={<TrashIcon size={12} />}
                        disabled={checkIfDeleteAllItemsIsDisabled()}
                    >
                        <span>{t("action:request.delete_all_items")}</span>
                    </Menu.Item>
                )}
                <Menu.Item key="link" className="py-8" icon={<LinkIcon size={12} />}>
                    <span>{isLinked ? t(`action:request.unlink_fields`) : t(`action:request.link_fields`)}</span>
                </Menu.Item>
            </>
        </Menu>
    )

    return (
        <Col {...columnProps}>
            {isHospitality ? (
                <HospitalityExpenseItemRow elem={elem} />
            ) : (
                <Dropdown getPopupContainer={getPopupAnchor()} className="flex items-center z-100" overlay={renderDropDownMenu()} trigger={["click"]}>
                    <Button>
                        <DotsIcon />
                    </Button>
                </Dropdown>
            )}
        </Col>
    )
}

export const FormAddExpenseItemRow = React.memo(FormAddExpenseItemRowBase)
